import React from 'react';

import {
  Backdrop,
  Box,
  Fade,
  Slide,
  Unstable_TrapFocus,
  useTheme,
} from '@mui/material';
import { useAppSelector } from 'hooks';
import { selectIsMobile } from 'reducers/main';
import { MODAL_BORDER_RADIUS } from 'embed/constants';

const ANIMATION_DURATION = 225;
const PADDING = '24px';

interface PopupModalProps {
  children: React.ReactNode;
  onClickAway?: () => void;
  open: boolean;
  position?: 'top' | 'bottom';
}

export const PopupModal = ({
  children,
  onClickAway,
  open,
  position,
}: PopupModalProps) => {
  const theme = useTheme();
  const isMobile = useAppSelector(selectIsMobile);
  const [showModal, setShowModal] = React.useState(open);

  const backdropRef = React.useRef<HTMLDivElement>();

  const backdropBorderRadius = React.useMemo(() => {
    return isMobile ? '0' : `${MODAL_BORDER_RADIUS}`;
  }, [isMobile]);

  const modalBorderRadius = React.useMemo(() => {
    switch (position) {
      case 'top':
        return `0 0 ${MODAL_BORDER_RADIUS} ${MODAL_BORDER_RADIUS}`;
      case 'bottom':
        if (isMobile) {
          return `${MODAL_BORDER_RADIUS} ${MODAL_BORDER_RADIUS} 0 0`;
        } else {
          return MODAL_BORDER_RADIUS;
        }
    }
  }, [isMobile, position]);

  const modalAlignSelf = React.useMemo(() => {
    switch (position) {
      case 'top':
        return 'flex-start';
      case 'bottom':
        return 'flex-end';
    }
  }, [position]);

  const modalTop = React.useMemo(() => {
    return position === 'top' ? '64px' : undefined;
  }, [position]);

  React.useEffect(() => {
    setShowModal(open);
  }, [open]);

  const animationDirection = position === 'top' ? 'down' : 'up';

  const handleClickAway = () => {
    if (onClickAway) {
      setShowModal(false);
      setTimeout(() => {
        onClickAway();
      }, ANIMATION_DURATION / 2);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleClickAway();
    }
  };

  return (
    <Unstable_TrapFocus open={open}>
      <Fade in={showModal} timeout={ANIMATION_DURATION / 2}>
        <Backdrop
          // Workaround for https://github.com/mui/material-ui/issues/33146
          aria-hidden={!open}
          onClick={handleClickAway}
          open={open}
          ref={backdropRef}
          sx={{
            backgroundColor: `${theme.palette.colors.grey[700]}80`,
            borderRadius: backdropBorderRadius,
            height: '100%',
            overflow: 'hidden',
            width: '100%',
            zIndex: 10000,
          }}
          tabIndex={-1}
        >
          <Slide
            container={backdropRef.current}
            direction={animationDirection}
            in={showModal}
            timeout={ANIMATION_DURATION}
          >
            <Box
              className='forethought-scrollbar'
              onClick={e => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              sx={{
                alignSelf: modalAlignSelf,
                background: theme.palette.colors.white,
                borderRadius: modalBorderRadius,
                boxSizing: 'border-box',
                display: 'flex',
                maxHeight: '85%',
                overflow: 'auto',
                padding: `${PADDING} ${PADDING} 0 ${PADDING}`,
                position: 'relative',
                top: modalTop,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  // Acts as padding so that the scrollable content doesn't get rid of the padding
                  '&::after': {
                    content: '""',
                    display: 'block',
                    height: PADDING,
                    width: '100%',
                  },
                  width: '100%',
                }}
              >
                {children}
              </Box>
            </Box>
          </Slide>
        </Backdrop>
      </Fade>
    </Unstable_TrapFocus>
  );
};
